// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/Thread.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { CheckCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type AskV2RailFinding } from "../rail/types";

/**
 * Same footprint as the static tool-result row in `AgentSteps` (`ToolResultStep`
 * when it is not an artifact link): `TOOL_CHIP_CLASS` plus the done check and
 * name/secondary labels. ask-v2 cannot import `routes/ask-puntt` (import wall),
 * so the classes are copied rather than a new card.
 */
const TOOL_CALL_ROW_CLASS =
  "flex w-full min-w-0 items-center gap-2 rounded-lg bg-transparent px-[11px] py-[7px]";

/**
 * Early verified findings, in the thread, as static text.
 *
 * Same row treatment as a settled tool call that does not open anything.
 * These are pending comments, not links: a `viewReviewId` must not turn the
 * row into a button.
 */
export function AskV2ThreadFindings({
  findings,
}: {
  findings: AskV2RailFinding[];
}) {
  const { t } = useTranslation("ui");
  const confidence = (severity: string) =>
    severity === "critical" || severity === "major"
      ? t("highConfidence")
      : severity === "minor"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <div
      className="my-3"
      data-testid="ask-v2-thread-found-so-far"
      role="status"
      aria-live="polite"
    >
      <Text
        as="p"
        size="1"
        weight="regular"
        className="px-[11px] text-puntt-neutral-gray-12"
        data-testid="ask-v2-thread-found-so-far-title"
      >
        {t("askV2.rail.foundSoFar")}
      </Text>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {findings.map((finding) => {
          const label = confidence(finding.severity);
          return (
            <div
              key={finding.intentId}
              className={TOOL_CALL_ROW_CLASS}
              data-testid={`ask-v2-thread-finding-${finding.intentId}`}
              aria-label={t("askV2.rail.findingAria", {
                severity: label,
                description: finding.description,
              })}
            >
              <CheckCircle
                size={14}
                weight="fill"
                className="shrink-0 text-puntt-neutral-grayA-11"
                aria-hidden
              />
              <Text
                as="span"
                size="1"
                weight="regular"
                title={label}
                className="min-w-0 max-w-[45%] shrink-0 truncate whitespace-nowrap text-puntt-neutral-gray-12"
              >
                {label}
              </Text>
              <Text
                as="span"
                size="1"
                title={finding.description}
                className="min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-9"
              >
                {finding.description}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}
